import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { forwardRef, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export interface TabItem {
	key: string;
	label: React.ReactNode;
	children: React.ReactNode;
	disabled?: boolean;
	icon?: React.ReactNode;
	closable?: boolean;
}

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	items: TabItem[];
	activeKey?: string;
	defaultActiveKey?: string;
	onChange?: (key: string) => void;
	onEdit?: (targetKey: string, action: "add" | "remove") => void;
	tabBarExtraContent?: React.ReactNode;
	size?: "small" | "middle" | "large";
	type?: "line" | "card";
	centered?: boolean;
	tabPosition?: "top" | "bottom" | "left" | "right";
	destroyInactiveTabPane?: boolean;
}

const Tabs = forwardRef<HTMLDivElement, TabsProps>(
	(
		{
			className,
			items,
			activeKey,
			defaultActiveKey,
			onChange,
			onEdit,
			tabBarExtraContent,
			size = "middle",
			type = "line",
			centered = false,
			tabPosition = "top",
			destroyInactiveTabPane = false,
			...props
		},
		ref,
	) => {
		const getIndexFromKey = (key: string | undefined) => {
			if (!key) return 0;
			const index = items.findIndex((item) => item.key === key);
			return index >= 0 ? index : 0;
		};

		const [selectedIndex, setSelectedIndex] = useState(
			getIndexFromKey(defaultActiveKey || activeKey),
		);

		// Sync with controlled activeKey
		useEffect(() => {
			if (activeKey !== undefined) {
				setSelectedIndex(getIndexFromKey(activeKey));
			}
		}, [activeKey, items]);

		const handleChange = (index: number) => {
			const item = items[index];
			if (item && !item.disabled) {
				if (activeKey === undefined) {
					setSelectedIndex(index);
				}
				onChange?.(item.key);
			}
		};

		const sizeStyles = {
			small: "text-sm",
			middle: "text-sm",
			large: "text-base",
		};

		const tabPadding = {
			small: "px-3 py-1.5",
			middle: "px-4 py-2",
			large: "px-5 py-2.5",
		};

		const isVertical = tabPosition === "left" || tabPosition === "right";

		return (
			<TabGroup
				as="div"
				ref={ref}
				selectedIndex={selectedIndex}
				onChange={handleChange}
				vertical={isVertical}
				className={cn(
					"w-full",
					isVertical && "flex",
					tabPosition === "right" && "flex-row-reverse",
					className,
				)}
				{...props}
			>
				{tabPosition !== "bottom" && (
					<div
						className={cn(
							"flex border-zinc-200 dark:border-zinc-700",
							!isVertical && "border-b",
							isVertical && "flex-col border-r",
							centered && "justify-center",
						)}
					>
						<TabList className={cn("flex", isVertical && "flex-col", !isVertical && "flex-1")}>
							{items.map((item) => (
								<Tab
									key={item.key}
									disabled={item.disabled}
									className={cn(
										"relative flex items-center gap-2 whitespace-nowrap transition-colors outline-none",
										tabPadding[size],
										sizeStyles[size],
										"data-[disabled]:cursor-not-allowed data-[disabled]:text-zinc-300 dark:data-[disabled]:text-zinc-600",
										"text-zinc-600 dark:text-zinc-400 data-[hover]:text-blue-500",
										"data-[selected]:text-blue-500",
										type === "line" && [
											"data-[selected]:after:absolute data-[selected]:after:bottom-0 data-[selected]:after:left-0 data-[selected]:after:right-0 data-[selected]:after:h-0.5 data-[selected]:after:bg-blue-500",
											isVertical &&
												"data-[selected]:after:bottom-auto data-[selected]:after:top-0 data-[selected]:after:right-0 data-[selected]:after:left-auto data-[selected]:after:w-0.5 data-[selected]:after:h-full",
										],
										type === "card" && [
											"border border-transparent rounded-t-lg -mb-px",
											"data-[selected]:bg-white dark:data-[selected]:bg-zinc-800",
											"data-[selected]:border-zinc-200 dark:data-[selected]:border-zinc-700",
											"data-[selected]:border-b-white dark:data-[selected]:border-b-zinc-800",
											"data-[hover]:border-zinc-200 dark:data-[hover]:border-zinc-700",
										],
									)}
								>
									{item.icon}
									{item.label}
									{item.closable && (
										<span
											onClick={(e) => {
												e.stopPropagation();
												onEdit?.(item.key, "remove");
											}}
											className="ml-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
										>
											×
										</span>
									)}
								</Tab>
							))}
						</TabList>
						{tabBarExtraContent && (
							<div className="ml-auto flex items-center">{tabBarExtraContent}</div>
						)}
					</div>
				)}

				<TabPanels className={cn("flex-1", !isVertical && "pt-4", isVertical && "pl-4")}>
					{items.map((item) => (
						<TabPanel key={item.key} unmount={destroyInactiveTabPane} className="outline-none">
							{item.children}
						</TabPanel>
					))}
				</TabPanels>

				{tabPosition === "bottom" && (
					<div
						className={cn(
							"flex border-zinc-200 dark:border-zinc-700",
							"border-t",
							centered && "justify-center",
						)}
					>
						<TabList className="flex flex-1">
							{items.map((item) => (
								<Tab
									key={item.key}
									disabled={item.disabled}
									className={cn(
										"relative flex items-center gap-2 whitespace-nowrap transition-colors outline-none",
										tabPadding[size],
										sizeStyles[size],
										"data-[disabled]:cursor-not-allowed data-[disabled]:text-zinc-300",
										"text-zinc-600 dark:text-zinc-400 data-[hover]:text-blue-500",
										"data-[selected]:text-blue-500 data-[selected]:after:absolute data-[selected]:after:top-0 data-[selected]:after:left-0 data-[selected]:after:right-0 data-[selected]:after:h-0.5 data-[selected]:after:bg-blue-500",
									)}
								>
									{item.icon}
									{item.label}
								</Tab>
							))}
						</TabList>
					</div>
				)}
			</TabGroup>
		);
	},
);

Tabs.displayName = "Tabs";

export { Tabs };
